import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import { local } from 'data/lib/local-json'
import notificationService from "data/notification"
import { PromiseWatch } from "./lib/promise-helpers"

// private module logic //

const id: string = "portfolioasset"
const profile = local("auth.profile")
const orgId   = _.get(profile, "organizationId");
const initialState: ApiResult<object[]> = { id, state: "init", value: [] }
const portfolioAssetStream = new BehaviorSubject<ApiResult<object[]>>(initialState)
const portfolioAssetEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})

const _patches_ = PromiseWatch()
const _fetches_ = PromiseWatch()

function clear(): void {
  portfolioAssetStream.next({ id, state: 'ok', value: [] })
}

function fetchPortfolioAssets(): Promise<object[] | { message: string }> {
  clear()
  portfolioAssetStream.next({ id, state: 'busy', value: portfolioAssetStream.getValue().value })
  let url = `portfolioasset`
  return Client.get(url)
  .then((records: object[]) => {
    portfolioAssetStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  })
}

function fetchOne(portfolioId: number): Promise<object | { message: string }> {
  const currentValue = portfolioAssetStream.getValue().value;
  portfolioAssetStream.next({ id, state: 'busy', value: currentValue })

  return _fetches_.watch('portfolio', Client.get(`portfolioasset?portfolioid=eq.${portfolioId}`)
    .then((portfolios: object[]) => {
      portfolioAssetStream.next({ id, state: 'ok', value: portfolios })
      return portfolios
    })
    .catch((err: { message: string }) => {
      portfolioAssetStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      return err
    })
  )
}

function create(portfolioid:number, assetid:number): Promise<object | { message: string }> {
  const streamVal: object[] = portfolioAssetStream.getValue().value

  const payload = {
    portfolioid,
    assetid,
    organizationid: orgId
  }
  portfolioAssetStream.next({ id, state: 'ok', value: _.unionBy([payload], streamVal, 'assetid') })

  return Client.post(`portfolioasset`, payload)
  .then((portfolioasset: object) => {
    push_undo({
      undofunc: () => deleteOne(portfolioid, assetid),
      redofunc: () => create(portfolioid, assetid)
    })
    // update already happened optimistically

    // don't understand this next line or see any effect from it.
    // portfolioAssetEventStream.next({ type: 'PORTFOLIO_CREATED', payload: portfolioasset })
      return portfolioasset
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      portfolioAssetStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      return err
    })
}

async function deleteOne(
  portfolioid: number,
  assetid: number
): Promise<boolean | { message: string }> {
  const streamVal: object[] = portfolioAssetStream.getValue().value

  portfolioAssetStream.next({ id, state: 'ok', value: _.reject(streamVal, {assetid}) })

  const payload = {
    portfolioid,
    assetid,
    organizationid: orgId
  }

  // update the db
  return _patches_.all(portfolioid).then(() =>
  Client.del(`portfolioasset?portfolioid=eq.${portfolioid}&assetid=eq.${assetid}`)
    .then((portfolio: object) => {
      push_undo({
        undofunc: () => {
          Client.post('portfolioasset', payload)
          portfolioAssetStream.next({ id, state: 'ok', value: streamVal })
        },
        redofunc: () => deleteOne(portfolioid, assetid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      portfolioAssetStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchPortfolioAssets()
      return err
    })
  )
}

// public module interface //

const portfolioAssetService = {
  stream: () => portfolioAssetStream.asObservable(),
  value: () => portfolioAssetStream.getValue().value,
  eventStream: () => portfolioAssetEventStream.asObservable(),
  clear,
  fetchOne,
  fetchPortfolioAssets,
  create,
  deleteOne
}

export default portfolioAssetService